import React, { PureComponent } from 'react';

import { cx, css } from 'emotion';
import { throttle } from 'lodash-es';

// Note: for consumers of this package via NPM, this Less file gets converted to a
// CSS file during the package "build" step defined in src/components/diff/package.json,
// and the import extension is changed to ".css".
import './styles.less';
import {
  ChunkEntry,
  DiffEntry,
  DiffInlineRenderProp,
  LineAnnotation,
  OnAddComment,
  OnCodeSelect,
  OnShowMoreLines,
} from '../../common/types';
import { getConflictsChecker } from '../../common/util/conflicts-checker';
import getGutterWidth from '../../common/util/get-gutter-width';
import { ContentWidthProvider } from '../../controllers/content-width-controller';

import ChunkLoader from './chunk-loader';

export type DiffProps = {
  activePermalink?: string;
  diff: DiffEntry;
  filePath?: string;
  tabSize?: number;
  inlineContent?: DiffInlineRenderProp;
  isSideBySide: boolean;
  onAddComment?: OnAddComment;
  onPermalinkClick?: (permalink: string) => void;
  onShowMoreLines: OnShowMoreLines;
  onCodeSelect?: OnCodeSelect;
  showPermalinks: boolean;
  fileAnnotations?: LineAnnotation[];
  isPrAnnotationsEnabled?: boolean;
  isColorBlindModeEnabled?: boolean;
  isSyntaxHighlightingEnabled?: boolean;
  isMultiChunkExpansionInDiffEnabled?: boolean;
};

type DiffState = {
  gutterWidth: number;
};

function getDummyChunk() {
  return {
    id: 'dummy-chunk',
    changes: [],
    newStart: 0,
    oldStart: 0,
    newLines: 0,
    oldLines: 0,
    content: '',
    extra: {
      before: { hasMoreLines: true },
      after: { hasMoreLines: false },
    },
    isDummyChunk: true,
  };
}

export const MIN_TAB_SIZE = 1;
export const MAX_TAB_SIZE = 8;

const BITKIT_DIFF_ROOT_CLASSNAME = 'bitkit-diff-wrapper-diff';

// TODO: Replace usage of unsafe lifecycle methods
// eslint-disable-next-line react/no-unsafe
export default class DiffContent extends PureComponent<DiffProps, DiffState> {
  static defaultProps = {
    filePath: '',
    // eslint-disable-next-line react/default-props-match-prop-types
    isSideBySide: false,
    // eslint-disable-next-line react/default-props-match-prop-types
    showPermalinks: false,
    // eslint-disable-next-line react/default-props-match-prop-types
    onShowMoreLines: () => {},
  };

  constructor(props: DiffProps) {
    super(props);

    const { diff, isSideBySide } = props;
    this.state = {
      gutterWidth: getGutterWidth(diff, isSideBySide),
    };
    this.onSelectionChange = this.onSelectionChange.bind(this);
  }

  componentDidMount(): void {
    if (!this.props.onCodeSelect) {
      return;
    }
    document.addEventListener('selectionchange', this.onSelectionChange, false);
  }

  // eslint-disable-next-line react/no-unsafe
  UNSAFE_componentWillReceiveProps(nextProps: DiffProps): void {
    const { diff, isSideBySide } = nextProps;
    this.setState({ gutterWidth: getGutterWidth(diff, isSideBySide) });
  }

  componentWillUnmount(): void {
    if (!this.props.onCodeSelect) {
      return;
    }
    document.removeEventListener(
      'selectionchange',
      this.onSelectionChange,
      false
    );
  }

  isBeingSelected = false;

  onSelectionChange = throttle(() => {
    if (!this.props.onCodeSelect) {
      return;
    }

    const selection = document.getSelection();
    const bitDiffContainer = this.containerRef.current;
    if (
      !bitDiffContainer ||
      !selection ||
      !selection.anchorNode ||
      !selection.focusNode ||
      selection.rangeCount === 0
    ) {
      if (this.isBeingSelected) {
        this.isBeingSelected = false;
        this.props.onCodeSelect(null);
      }
      return;
    }

    const selectedText = selection.toString();
    if (selectedText.length === 0) {
      if (this.isBeingSelected) {
        this.isBeingSelected = false;
        this.props.onCodeSelect(null);
      }
      return;
    }

    const selectionWithinEditor =
      bitDiffContainer.contains(selection.anchorNode) &&
      bitDiffContainer.contains(selection.focusNode);
    if (!selectionWithinEditor) {
      // Selection does not entirely fall within this diff viewer
      if (this.isBeingSelected) {
        // This was being selected within, but now we're outside it
        this.isBeingSelected = false;
        this.props.onCodeSelect(null);
      }
      return;
    }

    this.isBeingSelected = true;

    const selectStartElementContainer =
      selection.anchorNode instanceof Element
        ? selection.anchorNode
        : selection.anchorNode.parentElement;
    const selectEndElementContainer =
      selection.focusNode instanceof Element
        ? selection.focusNode
        : selection.focusNode.parentElement;
    const startLineEl = selectStartElementContainer?.closest(
      '[data-key="code-line"]'
    );
    const finishLineEl = selectEndElementContainer?.closest(
      '[data-key="code-line"]'
    );
    if (!startLineEl || !finishLineEl) {
      // We are still within the diff container, but are currently selecting an element not within a line.
      // Do not change selection; wait until user continues selecting
      return;
    }

    // Sidebyside has 2 line number components, unifed just 1
    const startLineNumbers = startLineEl.querySelectorAll<HTMLElement>(
      '[data-key="code-line-number"]'
    );
    const isSideBySide = startLineNumbers.length === 2;

    const startLineNumberNewOldEls = isSideBySide
      ? { old: startLineNumbers.item(0), new: startLineNumbers.item(1) }
      : { old: startLineNumbers.item(0), new: startLineNumbers.item(0) };

    const startSelectOldLineNumberText =
      startLineNumberNewOldEls.old?.dataset[
        isSideBySide ? 'lineNumber' : 'oldLineNumber'
      ];
    const startSelectNewLineNumberText =
      startLineNumberNewOldEls.new?.dataset[
        isSideBySide ? 'lineNumber' : 'newLineNumber'
      ];
    const startSelectOldLineNumber = startSelectOldLineNumberText
      ? parseInt(startSelectOldLineNumberText, 10)
      : null;
    const startSelectNewLineNumber = startSelectNewLineNumberText
      ? parseInt(startSelectNewLineNumberText, 10)
      : null;

    const endLineNumbers = finishLineEl.querySelectorAll<HTMLElement>(
      '[data-key="code-line-number"]'
    );
    const endLineNumberNewOldEls = isSideBySide
      ? { old: endLineNumbers.item(0), new: endLineNumbers.item(1) }
      : { old: endLineNumbers.item(0), new: endLineNumbers.item(0) };

    const endSelectOldLineNumberText =
      endLineNumberNewOldEls.old?.dataset[
        isSideBySide ? 'lineNumber' : 'oldLineNumber'
      ];
    const endSelectNewLineNumberText =
      endLineNumberNewOldEls.new?.dataset[
        isSideBySide ? 'lineNumber' : 'newLineNumber'
      ];
    const endSelectOldLineNumber = endSelectOldLineNumberText
      ? parseInt(endSelectOldLineNumberText, 10)
      : null;
    const endSelectNewLineNumber = endSelectNewLineNumberText
      ? parseInt(endSelectNewLineNumberText, 10)
      : null;

    if (
      (!startSelectNewLineNumber && !startSelectOldLineNumber) ||
      (!endSelectNewLineNumber && !endSelectOldLineNumber)
    ) {
      // This should never occur, and may signify there is a problem with the selectors.
      return;
    }

    const selectingReversed =
      selection.focusNode.compareDocumentPosition(selection.anchorNode) ===
      Node.DOCUMENT_POSITION_FOLLOWING;

    this.props.onCodeSelect({
      content: selectedText,
      reversed: selectingReversed,
      from: {
        oldLine: startSelectOldLineNumber!,
        newLine: startSelectNewLineNumber,
      },
      to: {
        oldLine: endSelectOldLineNumber!,
        newLine: endSelectNewLineNumber,
      },
    });
  }, 300);

  containerRef = React.createRef<HTMLDivElement>();

  renderChunks(): JSX.Element[] {
    const { diff, fileAnnotations, isPrAnnotationsEnabled } = this.props;
    const { chunks } = diff;
    const lastChunk = chunks[chunks.length - 1] as ChunkEntry | undefined; // can be undefined if chunks.length === 0
    const dummyEndChunk = getDummyChunk();

    /* CHECK AND FLAG CONFLICTS. REMOVE WHEN MERGE CONFLICTS API IS FUNCTIONAL */
    const conflictsChecker = getConflictsChecker();
    const checkedChunks = chunks.map(chunk => ({
      ...chunk,
      changes: chunk.changes && chunk.changes.map(conflictsChecker),
    }));

    const hasMoreLines = lastChunk?.extra?.after?.hasMoreLines || false;
    if (hasMoreLines) {
      checkedChunks.push(dummyEndChunk);
    }

    return checkedChunks.map(
      (chunk: ChunkEntry, chunkIndex: number): JSX.Element => {
        const {
          activePermalink,
          filePath,
          isSideBySide,
          inlineContent,
          onAddComment,
          onShowMoreLines,
          onPermalinkClick,
          showPermalinks,
          isSyntaxHighlightingEnabled,
          isMultiChunkExpansionInDiffEnabled,
        } = this.props;

        return (
          <ChunkLoader
            activePermalink={activePermalink}
            key={chunkIndex}
            chunk={chunk}
            chunkIndex={chunkIndex}
            filePath={filePath}
            inlineContent={inlineContent}
            isSideBySide={isSideBySide}
            onAddComment={onAddComment}
            onShowMoreLines={onShowMoreLines}
            onPermalinkClick={onPermalinkClick}
            showPermalinks={showPermalinks}
            fileAnnotations={fileAnnotations}
            isPrAnnotationsEnabled={isPrAnnotationsEnabled}
            isFirstChunk={chunkIndex === 0}
            isLastChunk={
              hasMoreLines && chunkIndex === checkedChunks.length - 1
            }
            isSyntaxHighlightingEnabled={isSyntaxHighlightingEnabled}
            isMultiChunkExpansionInDiffEnabled={
              isMultiChunkExpansionInDiffEnabled
            }
          />
        );
      }
    );
  }

  render() {
    const { isColorBlindModeEnabled, isSideBySide, tabSize } = this.props;
    const { gutterWidth } = this.state;

    // Rather than passing the calculated gutterWidth value down as a prop, it is provided via
    // CSS class names. Once IE 11 support is dropped (end of June 2020) CSS variables can be used
    // to cascade the gutterWidth value and the @emotion/babel-preset-css-prop entry can be removed
    // from babel.config.json
    const gutterWidthStyles = css`
      .gutter-width {
        &-apply-padding-left {
          padding-left: ${gutterWidth}px;
        }

        &-apply-width {
          width: ${gutterWidth}px;
        }

        &-apply-flex: {
          flex: 0 0 ${gutterWidth}px;
        }

        &-apply-left {
          left: ${gutterWidth}px;
        }

        &-apply-negative-left-margin {
          margin-left: ${-gutterWidth}px;
        }

        &-apply-max-width-calc {
          max-width: calc(100% - ${gutterWidth}px);
        }
      }
    `;

    const tabSizeClassName = `tab-size-${
      // Default 6 is a fallback, just in case.
      tabSize || 6
    }`;

    return (
      <ContentWidthProvider gutterWidth={gutterWidth}>
        <div
          className={cx(
            BITKIT_DIFF_ROOT_CLASSNAME,
            tabSizeClassName,
            gutterWidthStyles,
            {
              'feature-color-blind-mode': isColorBlindModeEnabled,
              'side-by-side': isSideBySide,
            }
          )}
        >
          {/* https://dequeuniversity.com/rules/axe/4.10/scrollable-region-focusable */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <div className="chunks-wrapper" tabIndex={0} ref={this.containerRef}>
            <div className="chunks-scroll-wrapper">{this.renderChunks()}</div>
          </div>
        </div>
      </ContentWidthProvider>
    );
  }
}
